<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_failures_title') }}
        </h4>
        <div class="d-flex flex-wrap">
          <b-button
            v-for="option in scopeOptions"
            :key="option.value"
            size="sm"
            class="mr-50 mb-50"
            :variant="scope === option.value ? 'primary' : 'outline-primary'"
            @click="scope = option.value"
          >
            {{ option.label }}
          </b-button>
        </div>
      </div>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!visibleRows.length"
        class="text-center text-muted py-4"
      >
        {{ emptyText }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="visibleRows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(source)="data">
              {{ sourceLabel(data.item) }}
            </template>
            <template #cell(source_id)="data">
              <b-link
                v-if="sourceRoute(data.item)"
                :to="sourceRoute(data.item)"
              >
                {{ data.item.source_id }}
              </b-link>
              <span v-else>{{ data.item.source_id }}</span>
            </template>
            <template #cell(error_message)="data">
              <span
                class="ledger-failure-error"
                dir="auto"
              >{{ data.item.error_message }}</span>
            </template>
            <template #cell(created_at)="data">
              {{ formatWhen(data.item.created_at) }}
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="canRetry(data.item)"
                size="sm"
                variant="primary"
                :disabled="retryingId === data.item.id"
                @click="retryRow(data.item)"
              >
                <b-spinner
                  v-if="retryingId === data.item.id"
                  small
                  class="mr-50"
                />
                {{ $t('ledger_failures_retry') }}
              </b-button>
              <b-link
                v-else-if="journalLink(data.item)"
                :to="journalLink(data.item)"
              >
                {{ $t('ledger_journal_open') }}
              </b-link>
              <span
                v-else-if="data.item.resolved_at"
                class="text-muted"
              >
                {{ $t('ledger_failures_resolved') }}
              </span>
              <span
                v-else
                class="text-muted"
              >—</span>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <article
            v-for="item in visibleRows"
            :key="item.id"
            class="ledger-failure-card"
          >
            <div class="d-flex justify-content-between align-items-start">
              <strong>{{ sourceLabel(item) }}</strong>
              <span class="text-muted small">{{ formatWhen(item.created_at) }}</span>
            </div>
            <div class="mt-50">
              <span class="text-muted">{{ $t('ledger_failures_number') }}: </span>
              <b-link
                v-if="sourceRoute(item)"
                :to="sourceRoute(item)"
              >
                {{ item.source_id }}
              </b-link>
              <span v-else>{{ item.source_id }}</span>
            </div>
            <p
              class="ledger-failure-error mb-0 mt-50"
              dir="auto"
            >
              {{ item.error_message }}
            </p>
            <b-button
              v-if="canRetry(item)"
              block
              variant="primary"
              class="ledger-failure-retry mt-1"
              :disabled="retryingId === item.id"
              @click="retryRow(item)"
            >
              <b-spinner
                v-if="retryingId === item.id"
                small
                class="mr-50"
              />
              {{ $t('ledger_failures_retry') }}
            </b-button>
            <b-link
              v-else-if="journalLink(item)"
              class="d-inline-block mt-1"
              :to="journalLink(item)"
            >
              {{ $t('ledger_journal_open') }}
            </b-link>
            <div
              v-else-if="item.resolved_at"
              class="text-muted small mt-1"
            >
              {{ $t('ledger_failures_resolved') }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BButton, BTable, BSpinner, BLink,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getGlPostingFailures, retryGlPostingFailure } from '@/api/accounting/glPostingFailures'
import {
  listFrom, enumLabel, postingSourceKind, apiErrorMessage,
} from '@/utils/ledger'

const SOURCE_KEYS = {
  expense: 'ledger_source_expense',
  income: 'ledger_source_income',
  transfer: 'ledger_source_transfer',
  transaction: 'ledger_source_transaction',
}

const SOURCE_ROUTES = {
  expense: 'accounting-expense-transactions-view',
  income: 'accounting-income-transactions-view',
  transfer: 'accounting-transfers-view',
  transaction: 'accounting-transactions-view',
}

export default {
  name: 'PostingFailures',
  components: {
    BCard, BButton, BTable, BSpinner, BLink,
  },
  data() {
    return {
      loading: false,
      rows: [],
      scope: 'open',
      retryingId: null,
    }
  },
  computed: {
    scopeOptions() {
      return [
        { value: 'open', label: this.$t('ledger_failures_filter_open') },
        { value: 'resolved', label: this.$t('ledger_failures_filter_resolved') },
        { value: 'all', label: this.$t('ledger_failures_filter_all') },
      ]
    },
    visibleRows() {
      if (this.scope === 'resolved') return this.rows.filter(row => row.resolved_at)
      if (this.scope === 'all') return this.rows
      return this.rows.filter(row => !row.resolved_at)
    },
    emptyText() {
      if (this.scope === 'open' || !this.rows.length) return this.$t('ledger_failures_empty')
      return this.$t('ledger_failures_empty_filter')
    },
    fields() {
      return [
        { key: 'source', label: this.$t('ledger_failures_source') },
        { key: 'source_id', label: this.$t('ledger_failures_number') },
        { key: 'error_message', label: this.$t('ledger_failures_error') },
        { key: 'created_at', label: this.$t('ledger_failures_date') },
        { key: 'actions', label: this.$t('ledger_failures_action') },
      ]
    },
  },
  mounted() {
    this.loadFailures()
  },
  methods: {
    sourceLabel(row) {
      const source = row && row.source_type
      const kind = postingSourceKind(source)
      if (SOURCE_KEYS[kind]) return this.$t(SOURCE_KEYS[kind])
      if (source && typeof source === 'object') return enumLabel(source, this.$i18n.locale) || '—'
      return source ? String(source) : '—'
    },
    sourceRoute(row) {
      const id = row && row.source_id
      if (!id) return null
      const name = SOURCE_ROUTES[postingSourceKind(row.source_type)]
      if (!name) return null
      return { name, params: { id } }
    },
    journalLink(row) {
      const id = row && row.journal_entry_id
      if (!id || !this.hasPermission('view journal_entries')) return null
      return { name: 'ledger-journals-view', params: { id } }
    },
    canRetry(row) {
      return !!(row && !row.resolved_at && this.hasPermission('retry gl_posting_failures'))
    },
    formatWhen(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const locale = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    async loadFailures() {
      this.loading = true
      try {
        const res = await getGlPostingFailures()
        this.rows = listFrom(res)
      } catch (error) {
        this.rows = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async retryRow(row) {
      if (!row || this.retryingId) return
      this.retryingId = row.id
      try {
        const res = await retryGlPostingFailure(row.id)
        const payload = res && res.data && !Array.isArray(res.data) ? res.data : {}
        const journalId = (payload.journal_entry && payload.journal_entry.id)
          || (payload.failure && payload.failure.journal_entry_id)
          || null
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('ledger_posted_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        if (journalId && this.hasPermission('view journal_entries')) {
          this.$router.push({ name: 'ledger-journals-view', params: { id: journalId } })
          return
        }
        await this.loadFailures()
      } catch (error) {
        const message = error && error.response && error.response.data && error.response.data.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: typeof message === 'string' && message
              ? message
              : apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.retryingId = null
      }
    },
  },
}
</script>

<style lang="scss">
.ledger-failure-error {
  white-space: normal;
  word-break: break-word;
}
.ledger-failure-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
}
.ledger-failure-retry {
  position: sticky;
  bottom: 0.5rem;
  z-index: 2;
  min-height: 44px;
}
</style>
